import React, { memo } from 'react';
import { useFrame } from '~/shared/utils/frame';
import ChatIcon from '$icons/chat.svg';
import { useTranslation } from '~/shared/utils';
import {
    StyledChatButton,
    StyledChatButtonContainer,
    StyledChatButtonMotionWrapper,
} from './styled';
import { useTheme } from '@emotion/react';
import { useCookieConsent, useIsScrolledDown } from '~/shared/hooks';
import { Zendesk } from './Zendesk';
import { ZendeskV1Api } from './ZendeskV1Api';
import { useChat } from './useChat';
import { Dixa } from './Dixa';

const ChatButton = memo(() => {
    const theme = useTheme();
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { functional } = useCookieConsent();
    const { showChat, chatState } = useChat();
    const isScrolledDown = useIsScrolledDown();

    const chatProvider = frame?.settings?.customerService?.chatProvider;

    // Don't show on mobile or if chatProvider is missing
    if (!chatProvider) {
        return null;
    }

    const transition = {
        type: 'spring',
        ...theme.animations.springDefault,
    };

    if (frame?.settings?.customerService?.zendeskScriptSource) {
        return (
            <ZendeskV1Api
                zendeskScriptSource={frame.settings.customerService.zendeskScriptSource}
            />
        );
    }

    return (
        <StyledChatButtonContainer
            style={{ overflow: 'hidden' }}
            animate={chatState}
            variants={{
                loading: {
                    opacity: 0.75,
                },
                open: {
                    y: '100%',
                    opacity: 0,
                },
            }}
            transition={transition}
        >
            <StyledChatButtonMotionWrapper
                animate={{ x: isScrolledDown ? 'calc(100% - 56px)' : '0%' }}
                transition={transition}
            >
                <StyledChatButton
                    onClick={showChat}
                    shape="icon"
                    style={{ pointerEvents: chatState === 'loading' ? 'none' : 'auto' }}
                    animate={{
                        x: isScrolledDown ? -11 : 0, // 12 would be the right amount, but visually 11 is better
                    }}
                    transition={transition}
                >
                    <ChatIcon aria-hidden="true" />
                    {translate('global.consent.chat.button')}
                </StyledChatButton>
            </StyledChatButtonMotionWrapper>
            {chatState !== 'pristine' && functional && (
                <>
                    {chatProvider === 'zendesk' && <Zendesk />}
                    {chatProvider === 'makesYouLocal' && <Dixa />}
                </>
            )}
        </StyledChatButtonContainer>
    );
});

export default ChatButton;
